import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ColumnData } from '../../../shared/models/column-data.model';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { KeyValuePipe, NgClass, NgForOf, NgIf, UpperCasePipe } from '@angular/common';
import { ColumnDataService } from '../../../shared/services/column-data.service';
import { VariableTypeEnum } from '../../../../shared/ontology/variable-type.enum';
import { getStaticColumnLabelKey } from '../../../../shared/util/static-columns-util';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export const COLUMN_SELECTOR_CHECKBOX_ID = 0;

@Component({
    selector: 'jhi-column-selector',
    standalone: true,
    imports: [
        NgbDropdownMenu,
        NgbDropdownToggle,
        NgbDropdown,
        NgClass,
        CdkDragHandle,
        CdkDropList,
        CdkDrag,
        FormsModule,
        UpperCasePipe,
        NgIf,
        NgForOf,
        TranslateModule,
        KeyValuePipe
    ],
    templateUrl: './column-selector.component.html',
    styleUrl: './column-selector.component.scss'
})
export class ColumnSelectorComponent {

    COLUMN_SELECTOR_CHECKBOX_ID = COLUMN_SELECTOR_CHECKBOX_ID;

    @Input() columnDataService: ColumnDataService;
    @Input() displayedColumns: ColumnData[] = [];
    @Input() showGroupToggle: boolean = true;
    @Input() enableHidingByGroup: boolean = false;
    @Output() changed: EventEmitter<any> = new EventEmitter();
    @Output() reset: EventEmitter<any> = new EventEmitter();
    @Output() close: EventEmitter<{ hasChanges: boolean}> = new EventEmitter();
    @Input() disabled: boolean = false;
    @Input() container: 'body' | null = 'body';

    searchTerm: string;
    isGrouped = false;
    hasChanges = false;


    protected environmentDetails: number[] = [];
    protected environmentConditions: number[] = [];
    protected traitsColumns: number[] = [];
    protected selectionsColumns: number[] = [];
    protected otherColumns: number[] = [];
    protected entryDetailsColumns: number[] = [];
    protected germplasmDescriptorColumns: number[] = [];
    protected passportColumns: number[] = [];
    protected attributesColumns: number[] = [];
    protected namesColumns: number[] = [];

    OTHER_COLUMNS_VARIABLE_TYPE = 1;
    NAME_VARIABLE_TYPE = 2;
    columnGroups: { name: string; columns: number[]; hidden: boolean }[] = [
        { name: 'study-editor.column-selector.groups.columns', columns: this.otherColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.germplasm-descriptors', columns: this.germplasmDescriptorColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.environment-details', columns: this.environmentDetails, hidden: false },
        { name: 'study-editor.column-selector.groups.environmental-conditions', columns: this.environmentConditions, hidden: false },
        { name: 'study-editor.column-selector.groups.entry-details', columns: this.entryDetailsColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.traits', columns: this.traitsColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.selections', columns: this.selectionsColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.passports', columns: this.passportColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.attributes', columns: this.attributesColumns, hidden: false },
        { name: 'study-editor.column-selector.groups.names', columns: this.namesColumns, hidden: false }
    ];

    columnGroupsMap: Map<number, any> = new Map<number, { name: string; columns: number[]; hidden: boolean }>([
        [this.OTHER_COLUMNS_VARIABLE_TYPE, { name: 'study-editor.column-selector.groups.columns', columns: this.otherColumns, hidden: false }],
        [VariableTypeEnum.GERMPLASM_DESCRIPTOR, { name: 'study-editor.column-selector.groups.germplasm-descriptors', columns: this.germplasmDescriptorColumns, hidden: false }],
        [VariableTypeEnum.ENVIRONMENT_DETAIL, { name: 'study-editor.column-selector.groups.environment-details', columns: this.environmentDetails, hidden: false }],
        [VariableTypeEnum.ENVIRONMENT_CONDITION, { name: 'study-editor.column-selector.groups.environmental-conditions', columns: this.environmentConditions, hidden: false }],
        [VariableTypeEnum.ENTRY_DETAIL, { name: 'study-editor.column-selector.groups.entry-details', columns: this.entryDetailsColumns, hidden: false }],
        [VariableTypeEnum.TRAIT, { name: 'study-editor.column-selector.groups.traits', columns: this.traitsColumns, hidden: false }],
        [VariableTypeEnum.SELECTION_METHOD, { name: 'study-editor.column-selector.groups.selections', columns: this.selectionsColumns, hidden: false }],
        [VariableTypeEnum.GERMPLASM_PASSPORT, { name: 'study-editor.column-selector.groups.passports', columns: this.passportColumns, hidden: false }],
        [VariableTypeEnum.GERMPLASM_ATTRIBUTE, { name: 'study-editor.column-selector.groups.attributes', columns: this.attributesColumns, hidden: false }],
        [this.NAME_VARIABLE_TYPE, { name: 'study-editor.column-selector.groups.names', columns: this.namesColumns, hidden: false }]
    ]);

    resetToDefault() {
        this.searchTerm = '';
        this.isGrouped = false;
        this.environmentDetails = [];
        this.environmentConditions = [];
        this.traitsColumns = [];
        this.selectionsColumns = [];
        this.otherColumns = [];
        this.entryDetailsColumns = [];
        this.germplasmDescriptorColumns = [];
        this.passportColumns = [];
        this.attributesColumns = [];
        this.namesColumns = [];
    }

    onSearchColumn(event: Event): void {
        const searchString = this.searchTerm.toLowerCase();
        if (searchString) {
            this.displayedColumns.forEach(column => {
                column.columnSelectorVisible = column.displayName.toLowerCase().includes(searchString);
            });
        } else {
            this.displayedColumns.forEach(column => column.columnSelectorVisible = true);
        }
    }

    resetColumns() {
        this.columnDataService.deleteColumnsStateForCurrentKey();
        this.onReset();
    }

    onOpenChange(isOpen: boolean) {
        if (isOpen) {
            // Reset hasChanges whenever the Column Selector dropdown is opened
            this.hasChanges = false;
        }
        if (!isOpen && this.close) {
            // When the Column Selector is closed, emit close event
            // and pass hasChanges flag
            this.close.emit({ hasChanges: this.hasChanges });
        }
    }

    onChanged(column: ColumnData) {
        // When a column is toggeled/dropped, we need to flag that column visibility/order has changed
        this.hasChanges = true;

        if (this.changed) {
            this.changed.emit(column);
        }

        // Whenever a column is changed, save the column order/selected state.
        this.columnDataService.saveColumnsState();
    }

    onReset() {
        if (this.reset) {
            this.reset.emit();
        }
    }

    dropColumn(event: CdkDragDrop<any[]>) {
        // Only handle drops if we're not in search mode and not disabled
        if (this.isSearchTermEmpty() && !this.disabled) {
            // Only move items that are currently visible and selected
            const visibleSelectedColumns = this.displayedColumns
                .filter(col => col.columnSelectorVisible && col.id !== COLUMN_SELECTOR_CHECKBOX_ID && col.selected);

            // If we found the columns at both indexes
            if (visibleSelectedColumns[event.previousIndex] && visibleSelectedColumns[event.currentIndex]) {
                // Get the actual indexes in the original array
                const fromIndex = this.displayedColumns.indexOf(visibleSelectedColumns[event.previousIndex]);
                const toIndex = this.displayedColumns.indexOf(visibleSelectedColumns[event.currentIndex]);

                // Perform the move operation
                moveItemInArray(this.displayedColumns, fromIndex, toIndex);

                // Save the new column order
                this.columnDataService.saveColumnsState();

                // Notify that columns have changed
                this.onChanged(null);
            }
        }
    }


    isSearchTermEmpty(): boolean {
        return !this.searchTerm || this.searchTerm?.trim().length === 0;
    }

    private groupColumns(columns: ColumnData[]): void {
        this.resetColumnGroupsMap();
        columns.forEach((columnData, index) => {
            if (!columnData) {
                return;
            }

            switch (columnData.variableTypeId) {
            case VariableTypeEnum.ENVIRONMENT_DETAIL:
            case VariableTypeEnum.ENVIRONMENT_CONDITION:
            case VariableTypeEnum.TRAIT:
            case VariableTypeEnum.SELECTION_METHOD:
            case VariableTypeEnum.ENTRY_DETAIL:
            case VariableTypeEnum.GERMPLASM_DESCRIPTOR:
            case VariableTypeEnum.GERMPLASM_ATTRIBUTE:
            case VariableTypeEnum.GERMPLASM_PASSPORT:
                const group = this.columnGroupsMap.get(columnData.variableTypeId);
                if (group) {
                    group.columns.push(index);
                }
                break;
            case null:
                if (columnData.termId > 0) {
                    const nameGroup = this.columnGroupsMap.get(this.NAME_VARIABLE_TYPE);
                    if (nameGroup) {
                        nameGroup.columns.push(index);
                    }
                    break;
                }
            default:
                const otherGroup = this.columnGroupsMap.get(this.OTHER_COLUMNS_VARIABLE_TYPE);
                if (otherGroup) {
                    otherGroup.columns.push(index);
                }
            }
        });

        this.columnGroupsMap.forEach((group) => {
            group.hidden = group.columns.length > 0 &&
                group.columns.every(index => !this.displayedColumns[index]?.selected);
        });
    }

    resetColumnGroupsMap() {
        for (const mapKey of this.columnGroupsMap.keys()) {
            this.columnGroupsMap.get(mapKey).columns = [];
            this.columnGroupsMap.get(mapKey).hidden = false;
        }
    }

    onGroupedChanged() {
        if (this.isGrouped) {
            this.groupColumns(this.displayedColumns);
        }
    }

    toggleDisplayGroupColumns(groupId: number, isHiding: boolean) {
        const colGroup = this.columnGroupsMap.get(groupId);
        colGroup.columns.forEach((index) => {
            this.displayedColumns[index].selected = !isHiding;
        });
        colGroup.hidden = isHiding;
        this.onChanged(null);
    }

    protected readonly Object = Object;
    protected readonly Number = Number;
    protected readonly getStaticColumnLabelKey = getStaticColumnLabelKey;
}
